import { useCallback, useRef } from 'react';

import apiToken, { handleApiError } from '@/shared/services/axiosInstance';

interface UpdateCartParams {
  cartUuid: string;
  quantity: number;
}

export const useCartUpdates = () => {
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateCartQuantity = useCallback(async (params: UpdateCartParams) => {
    try {
      await apiToken.patch('/api/v1/cart', params);
    } catch (error) {
      const errorMessage = handleApiError(error);
      console.error('Failed to update cart quantity:', errorMessage);
      throw new Error(errorMessage);
    }
  }, []);

  const debouncedUpdateCart = useCallback(
    (params: UpdateCartParams, delay = 500) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        updateCartQuantity(params).catch((error) => {
          const errorMessage = handleApiError(error);
          console.error('Debounced cart update failed:', errorMessage);
        });
      }, delay);
    },
    [updateCartQuantity],
  );

  const cancelPendingUpdate = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
  }, []);

  return {
    updateCartQuantity,
    debouncedUpdateCart,
    cancelPendingUpdate,
  };
};
